import { i18n } from "@/i18n";

const RADAR_FALLBACK_COLOR = "#999999";

// Couleur dédiée à chaque profil triad dans le radar de cluster (reprend les teintes
// utilisées par la version précédente de cette computed, pour ne pas changer le rendu visuel).
const PROFILE_COLORS = Object.freeze({
  1: "#6ac7a4", // apprenti
  2: "#00275e", // tuteur pédagogique
  3: "#da5164", // maître d'apprentissage
  4: "#f8a007", // gestionnaire Studea
});

const PROFILE_LABEL_KEYS = Object.freeze({
  1: "apprentice",
  2: "pedagogic_tutor",
  3: "apprenticeMaster",
  4: "studeaManager",
});

class QuestionnaireRadarService {
  /**
   * Résout la note d'une ligne de grille pour un axe du radar (QuestionnaireCluster{Editor,Reply}Component.radarChart).
   *
   * Invariant : `values` (arrayValue.answers d'une réponse, ou questionnaireModel[id].vars.value pour
   * le profil connecté) est toujours construit avec une entrée par colonne, à la même position que
   * `valueList` (cf. createClusterRowModel et questionnaireModelBuilder.buildField, qui initialisent
   * un slot par colonne). On revalide malgré tout l'égalité avec la valeur de la colonne à cette même
   * position, pour ignorer une réponse devenue orpheline si les colonnes ont été modifiées/réordonnées
   * depuis que la réponse a été enregistrée.
   */
  extractMark(values, valueList) {
    if (!Array.isArray(values) || !Array.isArray(valueList)) {
      return null;
    }
    const index = valueList.findIndex(
      (column, i) => values[i] !== "" && values[i] != null && values[i] === column.value,
    );
    return index > -1 ? Number(valueList[index].mark) : null;
  }

  // Plus le profil de l'utilisateur connecté s'il a lui-même répondu (sa réponse n'est jamais
  // dans fieldAnswers, elle vient du questionnaireModel courant, cf. `ownProfileHasAnswer`).
  collectRadarProfiles(questions, isAnswerSet, ownProfile, ownProfileHasAnswer) {
    const profiles = [];
    questions.forEach((question) => {
      (question.fieldAnswers || []).forEach((answer) => {
        if (isAnswerSet(answer) && !profiles.includes(answer.writerProfile)) {
          profiles.push(answer.writerProfile);
        }
      });
    });

    if (ownProfile && !profiles.includes(ownProfile) && ownProfileHasAnswer) {
      profiles.push(ownProfile);
    }

    return profiles.sort((a, b) => a - b);
  }

  /**
   * Volontairement indépendant de triadManager.setProfileLabel : triad-manager.js dépend
   * transitivement de @/main, qui charge le store questionnaire, qui importe ce service
   * (cycle d'import), et n'a pas besoin ici du libellé personnalisable par plateforme.
   */
  getProfileLabel(profile) {
    const key = PROFILE_LABEL_KEYS[profile];
    return key ? i18n.global.t(key) : "";
  }

  buildRadarSeries(questions, profiles, ownProfile, questionnaireModel, valueList, isAnswerSet) {
    const series = [];
    const colors = [];

    profiles.forEach((profile) => {
      const data = questions.map((question) => {
        if (profile === ownProfile) {
          return this.extractMark(questionnaireModel[question.id]?.vars?.value, valueList);
        }
        const answer = (question.fieldAnswers || []).find((item) => item.writerProfile === profile);
        if (!answer || !isAnswerSet(answer)) {
          return null;
        }
        return this.extractMark(answer.arrayValue?.answers, valueList);
      });

      series.push({
        name: this.getProfileLabel(profile),
        data: data,
      });
      colors.push(PROFILE_COLORS[profile] || RADAR_FALLBACK_COLOR);
    });

    return { series, colors };
  }
}

export const questionnaireRadarService = new QuestionnaireRadarService();
